import Decimal from 'decimal.js'
import { ORDER_SIDE } from '@/constants/order'

// https://docs.orderly.network/trade-on-orderly/perpetual-futures/margin-leverage-and-pnl

// https://docs.orderly.network/trade-on-orderly/perpetual-futures/margin-leverage-and-pnl#notional-value
export function calculatePositionNotionalValue(positionSize, markPrice) {
  return Decimal(positionSize).mul(markPrice).abs().toNumber()
}

// https://docs.orderly.network/trade-on-orderly/perpetual-futures/margin-leverage-and-pnl#initial-margin-ratio
export function calculateInitialMarginRatio({
  accountMaxLeverage,
  baseImr = 0,
  imrFactor = 0,
  netPositionNotionalValue = 0
}) {
  return accountMaxLeverage
    ? Decimal.max(
        Decimal(1).div(accountMaxLeverage),
        baseImr,
        Decimal(imrFactor).mul(
          Decimal(netPositionNotionalValue).abs().pow(0.8).toNumber()
        )
      ).toNumber()
    : 0
}

export function calculateUnrealPnl(
  positionQty = 0,
  markPrice = 0,
  averageOpenPrice = 0
) {
  return !Decimal(positionQty).eq(0)
    ? Decimal(positionQty)
        .mul(Decimal(markPrice).sub(averageOpenPrice))
        .toNumber()
    : 0
}

export function calculateUnSettledPnl({
  positionQty = 0,
  markPrice = 0,
  sumUnitaryFunding = 0,
  lastSumUnitaryFunding = 0,
  costPosition = 0
}) {
  return Decimal(positionQty)
    .mul(
      Decimal(markPrice).sub(
        Decimal(sumUnitaryFunding).sub(lastSumUnitaryFunding)
      )
    )
    .sub(costPosition)
    .toNumber()
}

// https://docs.orderly.network/trade-on-orderly/perpetual-futures/margin-leverage-and-pnl#maintenance-margin-ratio
export function calculateMaintenanceMarginRatio({
  baseMmr,
  baseImr,
  imrFactor,
  positionNotionalValue
}) {
  return Decimal.max(
    baseMmr,
    Decimal(baseMmr)
      .div(baseImr)
      .mul(imrFactor)
      .mul(Decimal(positionNotionalValue).abs().pow(0.8).toNumber())
  ).toNumber()
}

export function calculateMaintenanceMargin(
  positionNotionalValue,
  maintenanceMarginRatio
) {
  return Decimal(positionNotionalValue).mul(maintenanceMarginRatio).toNumber()
}

export function calculateLiquidationPrice({
  positionQty = 0,
  markPrice = 0,
  totalCollateral,
  maintenanceMarginRatio = 0,
  totalMaintenanceMargin
}) {
  if (
    Decimal(positionQty).eq(0) ||
    Decimal(markPrice).eq(0) ||
    Decimal(totalCollateral).eq(0) ||
    totalMaintenanceMargin === undefined
  ) {
    return 0
  }

  const netPositionQty = Decimal(positionQty).abs()
  const positionQtySign = Decimal(positionQty).gt(0) ? 1 : -1
  return Decimal(markPrice)
    .sub(
      Decimal(
        Decimal(totalCollateral)
          .sub(totalMaintenanceMargin)
          .div(netPositionQty)
          .div(
            Decimal(1).sub(Decimal(maintenanceMarginRatio).mul(positionQtySign))
          )
      ).mul(positionQtySign)
    )
    .toNumber()
}

// Order form calculator
export function calculationOrderLiquidationPrice({
  positionQty = 0,
  orderSide = ORDER_SIDE.buy,
  orderPrice,
  orderSize = 0,
  markPrice = 0,
  maintenanceMargin = 0,
  totalMaintenanceMargin,
  totalCollateral,
  symbolMM = 0, // spot margin ?
  symbolRealMMR = 0 // spot margin ?
}) {
  const orderSideSign = orderSide === ORDER_SIDE.buy ? 1 : -1
  const positionQtyResult = Decimal(orderSize)
    .mul(orderSideSign)
    .add(positionQty)

  if (positionQtyResult.eq(0)) {
    return 0
  }

  const positionSign = Decimal(positionQtyResult.gt(0) ? 1 : -1)
  const netPositionQty = positionQtyResult.abs()
  // const positionNotionalValue = netPositionQty.times(markPrice).toNumber()

  const estimateLiquidationPrice = Decimal(orderPrice || markPrice).sub(
    positionSign
      .mul(
        Decimal(totalCollateral).sub(
          Decimal(totalMaintenanceMargin).sub(maintenanceMargin).add(symbolMM)
        )
      )
      .div(netPositionQty)
      .div(Decimal(1).sub(positionSign.mul(symbolRealMMR)))
  )

  return Decimal.max(estimateLiquidationPrice, 0).toNumber()
}

export function calculateMaxOrderSize({
  markPrice,
  orderSide,
  positionQty = 0,
  pendingLongQty = 0,
  pendingShortQty = 0,
  accountMaxLeverage = 0,
  totalCollateral = 0, // futuresAdjustedCollateral
  totalInitialMargin = 0,
  otherPositionTotalInitialMargin
}) {
  pendingShortQty = Decimal(pendingShortQty).abs().toNumber()
  if (Decimal(totalCollateral).lt(totalInitialMargin)) {
    // Margin ratio under 10%
    if (
      (orderSide === ORDER_SIDE.buy && Decimal(positionQty).gte(0)) ||
      (orderSide === ORDER_SIDE.sell && Decimal(positionQty).lt(0))
    ) {
      return 0
    }

    if (orderSide === ORDER_SIDE.buy) {
      return Decimal.max(
        Decimal(positionQty).abs().sub(pendingLongQty),
        0
      ).toNumber()
    }

    return Decimal.max(
      Decimal(positionQty).add(Decimal(pendingShortQty).mul(-1)),
      0
    ).toNumber()
  }

  const possibleOrderSize = Decimal(totalCollateral)
    .sub(otherPositionTotalInitialMargin)
    .mul(accountMaxLeverage)
    .div(markPrice)

  const currentTotalPosition =
    orderSide === ORDER_SIDE.buy
      ? Decimal(-1).mul(positionQty).sub(pendingLongQty)
      : Decimal(positionQty).sub(pendingShortQty)

  const maxOrderSize = possibleOrderSize.mul(0.995).add(currentTotalPosition)
  return Decimal.max(maxOrderSize, 0).toNumber()
}

export function calculateNewLeverage({
  orderSide,
  orderSize,
  positionQty,
  markPrice,
  totalCollateral
}) {
  positionQty = Decimal(positionQty)
  const isSameSideOrder =
    (orderSide === ORDER_SIDE.buy && positionQty.gte(0)) ||
    (orderSide === ORDER_SIDE.sell && positionQty.lte(0))
  const netPositionQty = positionQty.abs()
  const newPositionQty = isSameSideOrder
    ? netPositionQty.add(orderSize)
    : Decimal(orderSize).sub(netPositionQty)

  return Decimal.max(
    0,
    Decimal.min(newPositionQty.mul(markPrice).div(totalCollateral), 999)
  ).toNumber()
}
